import { useState } from "react";
import { Button, Icon, Skeleton, Typography } from "@/components/atoms";
import { AccessLink } from "@/redux/apis/assistants/types";
import { useGetClientProfileQuery } from "@/redux/apis/clientProfile/clientProfileApi";
import { clsx } from "@/utils";
import { ClientAvatar } from "./ClientAvatar";

// DEV-1293: the stats card + Recent Activity are stubbed (no aggregation endpoint yet), so they're
// hidden until that iteration. Flip to true (and wire real data) when DEV-1293 lands.
const SHOW_STATS = false;

type StatRange = "week" | "month" | "year" | "all";

const RANGE_OPTIONS: { label: string; value: StatRange }[] = [
  { label: "This week", value: "week" },
  { label: "This month", value: "month" },
  { label: "This year", value: "year" },
  { label: "All time", value: "all" },
];

const capitalize = (value?: string) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : "");

const SummaryRow = ({ icon, label, value }: { icon: string; label: string; value?: string | null }) => (
  <div className="flex items-center gap-3">
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-neutral-gray">
      <Icon name={icon} size="md" className="text-neutral-black" />
    </span>
    <div className="min-w-0">
      <Typography variant="paragraph" className="text-neutral-silver-gray">
        {label}
      </Typography>
      <Typography variant="action" className="break-words font-semibold text-neutral-black">
        {value || "-"}
      </Typography>
    </div>
  </div>
);

const PreferenceRow = ({ label, value, action }: { label: string; value?: string | null; action?: React.ReactNode }) => (
  <div className="flex items-center justify-between gap-4 py-2">
    <Typography variant="paragraph" className="text-neutral-silver-gray">
      {label}
    </Typography>
    {action ?? (
      <Typography variant="action" className="text-right font-semibold text-neutral-dark-gray">
        {value || "-"}
      </Typography>
    )}
  </div>
);

const StatCell = ({ icon, value, label }: { icon: string; value: string; label: string }) => (
  <div className="flex items-start gap-3">
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-neutral-gray">
      <Icon name={icon} size="md" className="text-neutral-black" />
    </span>
    <div>
      <Typography variant="h3" className="font-semibold text-neutral-black">
        {value}
      </Typography>
      <Typography variant="paragraph" className="text-neutral-dark-gray">
        {label}
      </Typography>
    </div>
  </div>
);

interface ClientDetailPanelProps {
  link: AccessLink;
  onRemoveAccess: () => void;
  /**
   * Navigates into the full Manage-Client takeover. Omitted in the operator's read-only "EA's
   * Clients" tab, where the button is hidden entirely (view-only).
   */
  onManageClient?: () => void;
  /**
   * Whether the "Remove My Access" button is shown. Defaults to true (the EA's own view). The
   * operator's read-only "EA's Clients" tab passes false — an operator may view the EA's clients
   * but must not remove the EA's access.
   */
  canRemoveAccess?: boolean;
  /**
   * Where this panel is rendered, which controls where the action buttons live:
   *  - "panel"  (default): desktop inline side panel — actions sit in the header (unchanged).
   *  - "modal"  : tablet centered Modal — actions move to a right-aligned footer row.
   *  - "drawer" : mobile bottom-sheet Drawer — actions move to a stacked, full-width footer.
   * In the overlay variants a "Close" button is appended (needs `onClose`).
   */
  variant?: "panel" | "modal" | "drawer";
  /** Dismisses the overlay. Only used (and rendered as a "Close" button) in the modal/drawer variants. */
  onClose?: () => void;
}

/**
 * EA "My Clients" right detail panel — profile summary (real data), stats + recent activity.
 * Stats and recent activity have no confirmed backend endpoint, so they render as
 * placeholders/skeletons and are flagged as a follow-up.
 */
export const ClientDetailPanel = ({
  link,
  onRemoveAccess,
  onManageClient,
  canRemoveAccess = true,
  variant = "panel",
  onClose,
}: ClientDetailPanelProps) => {
  const { party } = link;
  const [range, setRange] = useState<StatRange>("month");
  const isOverlay = variant !== "panel";

  // Single source of truth for the mutation affordances so the desktop header and the modal/drawer
  // footer stay in lockstep — `readOnly` is honoured once here (via canRemoveAccess/onManageClient).
  const removeButton = (full?: boolean) =>
    canRemoveAccess ? (
      <Button
        variant="custom"
        size={isOverlay ? "md" : "sm"}
        startIcon="CloseCircle"
        className={clsx("bg-danger text-white hover:bg-danger-dark", full && "w-full")}
        onClick={onRemoveAccess}
      >
        Remove My Access
      </Button>
    ) : null;
  const manageButton = (full?: boolean) =>
    onManageClient ? (
      <Button variant="primary" size={isOverlay ? "md" : "sm"} endIcon="ArrowRight" className={clsx(full && "w-full")} onClick={onManageClient}>
        Manage Client
      </Button>
    ) : null;
  const closeButton = (full?: boolean) => (
    <Button variant="tertiary" size="md" className={clsx(full && "w-full")} onClick={onClose}>
      Close
    </Button>
  );

  // Real profile data for the managed client. May be gated server-side for EAs — degrade gracefully.
  // The scoped client-profile read is only authorized once the link is CONNECTED (the backend lets
  // the EA act as the client only after approval). Skip it otherwise so we don't fire a doomed 403.
  const isConnected = link.status === "CONNECTED";
  const { data: profile, isLoading, isError } = useGetClientProfileQuery({ accountId: party.uuid }, { skip: !isConnected });
  const prefs = profile?.preferences;
  const hasProfile = !!profile && !isError;

  // mobile/company/preferences live on the client profile, not the access DTO.
  const mobile = profile?.personal.mobile || profile?.personal.phone || "";
  const company = profile?.company.company_name || "";
  const radio = prefs?.radio_station ? prefs.radio_station : prefs?.radio;
  const airport = prefs?.airport_waiting_time
    ? `${prefs.airport_waiting_time}${prefs.airport_waiting_timeframe ? ` ${prefs.airport_waiting_timeframe}` : ""}`
    : "";

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Typography variant="h3" className="font-semibold text-neutral-black">
          {party.name}&apos;s Details
        </Typography>
        {/* Action group — desktop only. In the modal/drawer variants the actions move to the footer
            (see below). Entirely hidden in the operator's view-only "EA's Clients" tab, where an
            operator may view but neither remove the EA's access nor take over managing the client. */}
        {!isOverlay && (canRemoveAccess || onManageClient) && (
          <div className="flex flex-wrap gap-2">
            {removeButton()}
            {manageButton()}
          </div>
        )}
      </div>

      <div className={clsx("grid grid-cols-1 gap-5", SHOW_STATS && "lg:grid-cols-2")}>
        {/* Stats + recent activity are gone (DEV-1293), so details and preferences sit side-by-side
            like the dashboard's Your Details card. @container splits to two columns when wide enough,
            stacks in the narrow Modal/Drawer. */}
        <div className="@container rounded-lg border border-neutral-gray bg-neutral-surface-gray">
          <div className="grid grid-cols-1 @[640px]:grid-cols-[5fr_1px_7fr]">
            <div className="flex flex-col p-5">
              <div className="flex flex-col">
                <ClientAvatar name={party.name} avatar={party.avatar} size="lg" className="!h-[72px] !w-[72px]" />
                <Typography variant="paragraph" className="mt-3 text-neutral-silver-gray">
                  Passenger
                </Typography>
                <Typography variant="h2" className="font-semibold text-neutral-black">
                  {party.name}
                </Typography>
              </div>

              <div className="mt-6 flex flex-col gap-4">
                <SummaryRow icon="Sms" label="Email address" value={party.email} />
                <SummaryRow icon="Call" label="Mobile no." value={mobile} />
                <SummaryRow icon="Buildings" label="Company" value={company} />
              </div>
            </div>

            {/* Vertical divider when side-by-side, horizontal when stacked */}
            <div className="hidden py-5 @[640px]:block">
              <div className="h-full border-r border-neutral-gray" />
            </div>
            <div className="mx-5 border-b border-neutral-gray @[640px]:hidden" />

            <div className="flex flex-col p-5">
              {isLoading ? (
                <Skeleton variant="short" />
              ) : (
                <>
                  <PreferenceRow label="Seating Preference" value={hasProfile ? capitalize(prefs?.seating) : undefined} />
                  <PreferenceRow label="Radio Preference" value={hasProfile ? radio : undefined} />
                  <PreferenceRow label="Quiet Ride" value={hasProfile ? capitalize(prefs?.quiet_ride) : undefined} />
                  <PreferenceRow label="Vehicle Temp" value={hasProfile ? prefs?.vehicle_temp : undefined} />
                  <PreferenceRow label="Airport Waiting Time Preference" value={hasProfile ? airport : undefined} />
                  <PreferenceRow
                    label="Notes"
                    action={
                      prefs?.driver_notes ? (
                        <Typography variant="action" className="font-medium text-info">
                          View
                        </Typography>
                      ) : undefined
                    }
                  />
                </>
              )}
            </div>
          </div>
        </div>

        {/* Stats + Recent activity — hidden pending DEV-1293 (no aggregation endpoint yet). */}
        {SHOW_STATS && (
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-8 rounded-lg border border-neutral-gray bg-neutral-surface-gray p-6">
            {/* Time-range filter (UI only — no stats endpoint yet) */}
            <div className="flex flex-wrap gap-2.5">
              {RANGE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setRange(opt.value)}
                  className={clsx(
                    "rounded-lg border px-2 py-0.5 transition-opacity",
                    range === opt.value
                      ? "border-primary bg-primary-light text-primary"
                      : "border-neutral-dark-gray bg-neutral-surface-gray text-neutral-dark-gray opacity-60 hover:opacity-100"
                  )}
                >
                  <Typography variant="action" className="font-semibold">
                    {opt.label}
                  </Typography>
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <StatCell icon="Car" value="-" label="Total Trips Taken" />
              <StatCell icon="Routing" value="-" label="Total Distance Traveled" />
              <StatCell icon="Calendar" value="-" label="Upcoming Trips" />
              <StatCell icon="DollarCircle" value="-" label="Total Spent on Rides" />
            </div>
          </div>

          <div className="rounded-lg border border-neutral-gray bg-neutral-surface-gray p-5">
            <Typography variant="title" className="mb-4 font-semibold text-neutral-dark-gray">
              Recent Activity
            </Typography>
            {/* No recent-activity endpoint yet — placeholder empty state. */}
            <div className="flex flex-col items-center gap-1 py-6 text-center">
              <Icon name="Clock" size="lg" className="text-neutral-silver-gray" />
              <Typography variant="paragraph" className="text-neutral-dark-gray">
                Recent activity will appear here once available.
              </Typography>
            </div>
          </div>
        </div>
        )}
      </div>

      {/* Overlay footer — modal: right-aligned row [Close · Remove · Manage]; drawer: stacked,
          full-width [Manage · Remove · Close]. readOnly collapses to just Close. */}
      {isOverlay &&
        (variant === "drawer" ? (
          <div className="flex flex-col gap-2 border-t border-neutral-gray pt-4">
            {manageButton(true)}
            {removeButton(true)}
            {closeButton(true)}
          </div>
        ) : (
          <div className="flex flex-wrap justify-end gap-2 border-t border-neutral-gray pt-4">
            {closeButton()}
            {removeButton()}
            {manageButton()}
          </div>
        ))}
    </div>
  );
};
